import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import TerminalLinkPage from "@/pages/TerminalLinkPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import TerminalPage from "@/pages/TerminalPage";
import FundsPage from "@/pages/FundsPage";
import DepositPage from "@/pages/DepositPage";
import WithdrawPage from "@/pages/WithdrawPage";
import ManagerPage from "@/pages/ManagerPage";
import ClientProfilePage from "@/pages/ClientProfilePage";
import SecurityPage from "@/pages/SecurityPage";
import SettingsPage from "@/pages/SettingsPage";
import CalendarPage from "@/pages/CalendarPage";
import HelpPage from "@/pages/HelpPage";
import InfoPage from "@/pages/InfoPage";
import DocumentsPage from "@/pages/DocumentsPage";
import DemoAccountPage from "@/pages/DemoAccountPage";
import AccountPage from "@/pages/AccountPage";
import { ComingSoonPage } from "@/pages/ComingSoonPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminInviteCodesPage from "@/pages/admin/AdminInviteCodesPage";
import AdminProvidersPage from "@/pages/admin/AdminProvidersPage";
import AdminBrandsPage from "@/pages/admin/AdminBrandsPage";
import AdminSupAuditPage from "@/pages/admin/AdminSupAuditPage";
import AdminAssetsPage from "@/pages/admin/AdminAssetsPage";
import AdminTeamPage from "@/pages/admin/AdminTeamPage";
import AdminTradingPage from "@/pages/admin/AdminTradingPage";
import AdminMarketConfigPage from "@/pages/admin/AdminMarketConfigPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import TeamMemberPage from "@/pages/TeamMemberPage";
import SupportChatPage from "@/pages/SupportChatPage";
import InboxPage from "@/pages/InboxPage";

const HOME = { admin: "/admin", manager: "/manager", agent: "/manager", user: "/terminal" };
const Entry = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={HOME[user.role] || "/terminal"} replace />;
};

const NO_ADMIN = ["admin"];
const NO_CLIENT = ["user"];
const NO_STAFF = ["admin", "supervisor", "manager", "agent"];

const SOON = [];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeProvider>
          <LanguageProvider>
          <BrandingProvider>
          <Toaster theme="dark" position="top-right" />
          <Routes>
            <Route path="/" element={<Entry />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/t/:token" element={<TerminalLinkPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/terminal" element={<ProtectedRoute blockRoles={NO_ADMIN}><TerminalPage /></ProtectedRoute>} />
            <Route path="/funds" element={<ProtectedRoute blockRoles={NO_ADMIN}><FundsPage /></ProtectedRoute>} />
            <Route path="/deposit" element={<ProtectedRoute blockRoles={NO_ADMIN}><DepositPage /></ProtectedRoute>} />
            <Route path="/withdraw" element={<ProtectedRoute blockRoles={NO_ADMIN}><WithdrawPage /></ProtectedRoute>} />
            <Route path="/manager" element={<ProtectedRoute><ManagerPage /></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><ClientProfilePage /></ProtectedRoute>} />
            <Route path="/clients/:id" element={<ProtectedRoute><ClientProfilePage /></ProtectedRoute>} />
            <Route path="/security" element={<ProtectedRoute blockRoles={NO_CLIENT}><SecurityPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><InfoPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
            <Route path="/demo-account" element={<ProtectedRoute><DemoAccountPage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute blockRoles={NO_ADMIN}><AccountPage /></ProtectedRoute>} />
            {SOON.map((s) => (
              <Route key={s.path} path={s.path} element={
                <ProtectedRoute><ComingSoonPage title={s.title} description={s.description} stage={s.stage} /></ProtectedRoute>
              } />
            ))}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute adminOnly><AdminTeamPage /></ProtectedRoute>} />
            <Route path="/admin/brands" element={<ProtectedRoute adminOnly><AdminBrandsPage /></ProtectedRoute>} />
            <Route path="/admin/sup-audit" element={<ProtectedRoute adminOnly><AdminSupAuditPage /></ProtectedRoute>} />
            <Route path="/admin/trading" element={<ProtectedRoute adminOnly><AdminTradingPage /></ProtectedRoute>} />
            <Route path="/admin/invite-codes" element={<ProtectedRoute adminOnly><AdminInviteCodesPage /></ProtectedRoute>} />
            <Route path="/admin/providers" element={<ProtectedRoute adminOnly><AdminProvidersPage /></ProtectedRoute>} />
            <Route path="/admin/assets" element={<ProtectedRoute adminOnly><AdminAssetsPage /></ProtectedRoute>} />
            <Route path="/admin/market-config" element={<ProtectedRoute adminOnly><AdminMarketConfigPage /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute adminOnly><AdminPaymentsPage /></ProtectedRoute>} />
            <Route path="/admin/documents" element={<ProtectedRoute adminOnly><AdminDocumentsPage /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettingsPage /></ProtectedRoute>} />
            <Route path="/team/:id" element={<ProtectedRoute blockRoles={NO_CLIENT}><TeamMemberPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute blockRoles={NO_STAFF}><SupportChatPage /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute blockRoles={NO_CLIENT}><InboxPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrandingProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
